/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as glob from '../../../../base/common/glob.js';
import { Event } from '../../../../base/common/event.js';
import { IDisposable } from '../../../../base/common/lifecycle.js';
import { Schemas } from '../../../../base/common/network.js';
import { posix } from '../../../../base/common/path.js';
import { basename } from '../../../../base/common/resources.js';
import { URI } from '../../../../base/common/uri.js';
import { localize } from '../../../../nls.js';
import { workbenchConfigurationNodeBase } from '../../../common/configuration.js';
import { Extensions as ConfigurationExtensions, IConfigurationNode, IConfigurationRegistry } from '../../../../platform/configuration/common/configurationRegistry.js';
import { IResourceEditorInput, ITextResourceEditorInput } from '../../../../platform/editor/common/editor.js';
import { createDecorator } from '../../../../platform/instantiation/common/instantiation.js';
import { Registry } from '../../../../platform/registry/common/platform.js';
import { EditorInputWithOptions, EditorInputWithOptionsAndGroup, IResourceDiffEditorInput, IResourceMultiDiffEditorInput, IResourceMergeEditorInput, IUntitledTextResourceEditorInput, IUntypedEditorInput } from '../../../common/editor.js';
import { IEditorGroup } from './editorGroupsService.js';
import { PreferredGroup } from './editorService.js';
import { AtLeastOne } from '../../../../base/common/types.js';

export const IEditorResolverService = createDecorator<IEditorResolverService>('editorResolverService');

//#region Editor Associations

// Static values for registered editors

export type EditorAssociation = {
	readonly viewType: string;
	readonly filenamePattern?: string;
};

export type EditorAssociations = readonly EditorAssociation[];

export const editorsAssociationsSettingId = 'workbench.editorAssociations';
export const diffEditorsAssociationsSettingId = 'workbench.diffEditorAssociations';

/**
 * Setting that controls whether the Markdown editor is the default editor for
 * `*.md` files in the Agents window. Gated behind an experiment so it can be
 * rolled out gradually. Defaults to on.
 */
export const markdownDefaultEditorAgentsWindowSettingId = 'workbench.editor.markdownDefaultEditorInAgentsWindow';

/**
 * Builds the default value for `workbench.editorAssociations` in the Agents window.
 * Shared so that dynamic re-registrations of the setting preserve the override.
 *
 * Each editor association can be toggled independently. Passing `undefined`
 * leaves the association at its enabled default, so the static registration
 * ends up with all defaults registered. Pass `false` to fall back to the
 * markdown preview editor for `*.md` files.
 */
export function editorsAssociationsAgentsWindowDefault(options?: { markdownDefaultEditor?: boolean }): Record<string, string> {
	return {
		'*.md': options?.markdownDefaultEditor === true ? 'vscode.markdown.editor' : 'vscode.markdown.preview.editor'
	};
}

const configurationRegistry = Registry.as<IConfigurationRegistry>(ConfigurationExtensions.Configuration);

const editorAssociationsConfigurationNode: IConfigurationNode = {
	...workbenchConfigurationNodeBase,
	properties: {
		[markdownDefaultEditorAgentsWindowSettingId]: {
			type: 'boolean',
			default: true,
			tags: ['experimental'],
			experiment: { mode: 'startup' },
			markdownDescription: localize('editor.markdownDefaultEditorInAgentsWindow', "Controls whether the Markdown editor is used as the default editor for Markdown files in the Agents window."),
		},
		[editorsAssociationsSettingId]: {
			type: 'object',
			markdownDescription: localize('editor.editorAssociations', "Configure [glob patterns](https://aka.ms/vscode-glob-patterns) to editors (for example `\"*.hex\": \"hexEditor.hexedit\"`). These have precedence over the default behavior."),
			additionalProperties: {
				type: 'string'
			},
			agentsWindow: {
				default: editorsAssociationsAgentsWindowDefault()
			}
		},
		[diffEditorsAssociationsSettingId]: {
			type: 'object',
			markdownDescription: localize('editor.diffEditorAssociations', "Configure [glob patterns](https://aka.ms/vscode-glob-patterns) to editors for diff views (for example `\"*.md\": \"vscode.markdown.preview.editor\"`). These override `workbench.editorAssociations` for diffs."),
			additionalProperties: {
				type: 'string'
			}
		}
	}
};

export interface IEditorType {
	readonly id: string;
	readonly displayName: string;
	readonly providerDisplayName: string;
}

configurationRegistry.registerConfiguration(editorAssociationsConfigurationNode);
//#endregion

//#region EditorResolverService types
export enum RegisteredEditorPriority {
	builtin = 'builtin',
	option = 'option',
	exclusive = 'exclusive',
	default = 'default',
	/**
	 * The editor is never automatically used for this kind of input, and it is
	 * also skipped when the user points a `workbench.editorAssociations` entry at
	 * it. Unlike `option`, a `never` editor is only used when it is the target of
	 * the specialized `workbench.diffEditorAssociations` setting or when the user
	 * explicitly opens it (for example via `Reopen Editor With`).
	 */
	never = 'never'
}

/**
 * If we didn't resolve an editor dictates what to do with the opening state
 * ABORT = Do not continue with opening the editor
 * NONE = Continue as if the resolution has been disabled as the service could not resolve one
 */
export const enum ResolvedStatus {
	ABORT = 1,
	NONE = 2,
}

export type ResolvedEditor = EditorInputWithOptionsAndGroup | ResolvedStatus;

export type RegisteredEditorOptions = {
	/**
	 * If your editor cannot be opened in multiple groups for the same resource
	 */
	singlePerResource?: boolean | (() => boolean);

	/**
	 * Whether or not you can support opening the given resource.
	 * If omitted we assume you can open everything
	 */
	canSupportResource?: (resource: URI) => boolean;
};

export type RegisteredEditorPriorityInfo = {
	readonly editor: RegisteredEditorPriority;
	readonly diff: RegisteredEditorPriority;
	readonly merge: RegisteredEditorPriority;
};

export type RegisteredEditorInfo = {
	readonly id: string;
	readonly label: string;
	readonly detail?: string;
	readonly priority: RegisteredEditorPriorityInfo;
};

export type RegisteredEditorRegistrationInfo = Omit<RegisteredEditorInfo, 'priority'> & {
	readonly priority: RegisteredEditorPriority | RegisteredEditorPriorityInfo;
};

export function toRegisteredEditorPriorityInfo(priority: RegisteredEditorPriority | RegisteredEditorPriorityInfo): RegisteredEditorPriorityInfo {
	if (typeof priority !== 'string') {
		return priority;
	}
	return {
		editor: priority,
		diff: priority,
		merge: priority,
	};
}

type EditorInputFactoryResult = EditorInputWithOptions | Promise<EditorInputWithOptions>;

export type EditorInputFactoryFunction = (editorInput: IResourceEditorInput | ITextResourceEditorInput, group: IEditorGroup) => EditorInputFactoryResult;

export type UntitledEditorInputFactoryFunction = (untitledEditorInput: IUntitledTextResourceEditorInput, group: IEditorGroup) => EditorInputFactoryResult;

export type DiffEditorInputFactoryFunction = (diffEditorInput: IResourceDiffEditorInput, group: IEditorGroup) => EditorInputFactoryResult;

export type MultiDiffEditorInputFactoryFunction = (multiDiffEditorInput: IResourceMultiDiffEditorInput, group: IEditorGroup) => EditorInputFactoryResult;

export type MergeEditorInputFactoryFunction = (mergeEditorInput: IResourceMergeEditorInput, group: IEditorGroup) => EditorInputFactoryResult;

type EditorInputFactories = {
	createEditorInput?: EditorInputFactoryFunction;
	createUntitledEditorInput?: UntitledEditorInputFactoryFunction;
	createDiffEditorInput?: DiffEditorInputFactoryFunction;
	createMultiDiffEditorInput?: MultiDiffEditorInputFactoryFunction;
	createMergeEditorInput?: MergeEditorInputFactoryFunction;
};

export type EditorInputFactoryObject = AtLeastOne<EditorInputFactories>;

export interface IEditorResolverService {
	readonly _serviceBrand: undefined;
	/**
	 * Given a resource finds the editor associations that match it from the user's settings
	 * @param resource The resource to match
	 * @return The matching associations
	 */
	getAssociationsForResource(resource: URI): EditorAssociations;

	/**
	 * Returns the view type of the user-configured default editor for a resource, or `undefined` when
	 * none is configured. When `forDiffEditor` is `true` the diff editor association setting
	 * (`workbench.diffEditorAssociations`) is consulted instead of the general one.
	 * @param resource The resource to match
	 * @param forDiffEditor Whether to read the diff editor association setting
	 */
	getConfiguredDefaultEditor(resource: URI, forDiffEditor?: boolean): string | undefined;

	/**
	 * Updates the user's association to include a specific editor ID as a default for the given glob pattern
	 * @param globPattern The glob pattern (must be a string as settings don't support relative glob)
	 * @param editorID The ID of the editor to make a user default
	 * @param forDiffEditor When `true`, the diff editor association (`workbench.diffEditorAssociations`)
	 * is updated instead of the general editor association (`workbench.editorAssociations`).
	 */
	updateUserAssociations(globPattern: string, editorID: string, forDiffEditor?: boolean): void;

	/**
	 * Emitted when an editor is registered or unregistered.
	 */
	readonly onDidChangeEditorRegistrations: Event<void>;

	/**
	 * Given a callback, run the callback pausing the registration emitter
	 */
	bufferChangeEvents(callback: Function): void;

	/**
	 * Registers a specific editor. Editors with the same glob pattern and ID will be grouped together by the resolver.
	 * This allows for registration of the factories in different locations
	 * @param globPattern The glob pattern for this registration
	 * @param editorInfo Information about the registration
	 * @param options Specific options which apply to this registration
	 * @param editorFactoryObject The editor input factory functions
	 */
	registerEditor(
		globPattern: string | glob.IRelativePattern,
		editorInfo: RegisteredEditorRegistrationInfo,
		options: RegisteredEditorOptions,
		editorFactoryObject: EditorInputFactoryObject
	): IDisposable;

	/**
	 * Given an editor resolves it to the suitable ResolvedEditor based on user extensions, settings, and built-in editors
	 * @param editor The editor to resolve
	 * @param preferredGroup The group you want to open the editor in
	 * @returns An EditorInputWithOptionsAndGroup if there is an available editor or a status of how to proceed
	 */
	resolveEditor(editor: IUntypedEditorInput, preferredGroup: PreferredGroup | undefined): Promise<ResolvedEditor>;

	/**
	 * Given a resource returns all the editor ids that match that resource. If there is exclusive editor we return an empty array
	 * @param resource The resource
	 * @returns A list of editor ids
	 */
	getEditors(resource: URI): RegisteredEditorInfo[];

	/**
	 * A set of all the editors that are registered to the editor resolver.
	 */
	getEditors(): RegisteredEditorInfo[];

	/**
	 * Returns the id of the best editor that can render a *diff* for the resource, excluding the
	 * built-in default text editor. This intentionally includes editors that opted out of diffs via a
	 * `never` priority: such editors opt out for text files, but when the default text diff editor
	 * cannot render the content (e.g. it is binary) a custom diff editor is preferable to the generic
	 * "cannot display" fallback. Returns `undefined` when no such (diff-capable) editor exists.
	 * @param resource The resource being diffed
	 */
	getBinaryDiffFallbackEditor(resource: URI): string | undefined;

	/**
	 * Get a complete list of editor associations.
	 */
	getAllUserAssociations(): EditorAssociations;
}

//#endregion

//#region Util functions
export function priorityToRank(priority: RegisteredEditorPriority): number {
	switch (priority) {
		case RegisteredEditorPriority.exclusive:
			return 5;
		case RegisteredEditorPriority.default:
			return 4;
		case RegisteredEditorPriority.builtin:
			return 3;
		// Text editor is priority 2
		case RegisteredEditorPriority.option:
			return 1;
		case RegisteredEditorPriority.never:
			return 0;
		default:
			return 1;
	}
}

export function globMatchesResource(globPattern: string | glob.IRelativePattern, resource: URI): boolean {
	const excludedSchemes = new Set([
		Schemas.extension,
		Schemas.webviewPanel,
		Schemas.vscodeWorkspaceTrust,
		Schemas.vscodeSettings
	]);
	// We want to say that the above schemes match no glob patterns
	if (excludedSchemes.has(resource.scheme)) {
		return false;
	}
	const matchOnPath = typeof globPattern === 'string' && globPattern.indexOf(posix.sep) >= 0;
	const target = matchOnPath ? `${resource.scheme}:${resource.path}` : basename(resource);
	return glob.match(globPattern, target, { ignoreCase: true });
}
//#endregion
